/**
 * Pure parser for the OIDC callback fragment.
 *
 * nyx-platform's /api/auth/oidc/callback redirects back with `#token=<jwt>`
 * in the URL fragment — never the query string. Fragments are never sent to
 * the server (not in access logs, not in proxy logs, not in the Referer
 * header on the next navigation), which is the whole point: query-string
 * tokens are a leak vector for public demo users (Design-Doc Befund 12).
 *
 * This module has no DOM/browser dependency so it can be unit tested without
 * jsdom; AuthContext.jsx is the only caller and supplies window.location.hash.
 */

/**
 * @param {string} hash - window.location.hash, with or without the leading '#'
 * @returns {{ token: string, remainingHash: string } | null} null when no
 *   token is present in the fragment; otherwise the token plus whatever else
 *   was in the fragment (serialized, without a leading '#') so callers can
 *   rebuild the URL without clobbering unrelated fragment state.
 */
export function extractOidcCallbackToken(hash) {
  const raw = typeof hash === 'string' && hash.startsWith('#') ? hash.slice(1) : (hash || '');
  const params = new URLSearchParams(raw);
  const token = params.get('token');
  if (!token) return null;
  params.delete('token');
  return { token, remainingHash: params.toString() };
}
